import { matchSorter } from 'match-sorter'

import { SharedAssetRequestStatus } from 'types/api/sharedAssets/requests'

import type {
  IncomingRequestByAssetId,
  OutgoingRequestByAssetId,
  TeamWithSharedAssetsInformation
} from 'hooks/use-manage-shared-assets'
import type {
  AvailableK8sClusterAsset,
  OwnedK8sClusterAsset
} from 'types/api/sharedAssets/sharedAssets'

export const NAMESPACE_CHIP_LIST_MAX_HEIGHT = 350
export const CLUSTER_LIST_PAGE_SIZE = 5

export enum ShareableListType {
  SHARED = 'shared',
  NOT_SHARED = 'not_shared'
}

export function getPendingNamespaceRequestsFromTeam(
  activeTeam: TeamWithSharedAssetsInformation,
  pendingNamespaceRequestsToRemove: IncomingRequestByAssetId[]
) {
  const pendingNamespacesToRemove = new Set(
    pendingNamespaceRequestsToRemove.map(({ namespace }) => namespace)
  )

  return activeTeam.requests[SharedAssetRequestStatus.REQUESTED].filter(
    (pendingRequest) => !pendingNamespacesToRemove.has(pendingRequest.namespace)
  )
}

export function getApprovedNamespaceRequestsFromTeam(
  activeTeam: TeamWithSharedAssetsInformation,
  approvedNamespaceRequestsToRemove: IncomingRequestByAssetId[]
) {
  const approvedNamespacesToRemove = new Set(
    approvedNamespaceRequestsToRemove.map(({ namespace }) => namespace)
  )

  return activeTeam.requests[SharedAssetRequestStatus.APPROVED].filter(
    (approvedRequest) =>
      !approvedNamespacesToRemove.has(approvedRequest.namespace)
  )
}

export function getSubmitButtonText(
  pendingNamespaceRequests: IncomingRequestByAssetId[],
  approvedNamespaceRequestsToRemove: IncomingRequestByAssetId[],
  selectedNamespacesToAdd: string[]
): string {
  if (
    (approvedNamespaceRequestsToRemove.length > 0 ||
      selectedNamespacesToAdd.length > 0) &&
    pendingNamespaceRequests.length > 0
  ) {
    return 'Approve And Save'
  }

  if (pendingNamespaceRequests.length > 0) {
    return 'Approve'
  }

  return 'Save'
}

export function filterNamespaces(
  namespaces: string[],
  searchValue: string
): string[] {
  if (!searchValue) {
    return namespaces
  }

  return matchSorter(namespaces, searchValue, {
    threshold: matchSorter.rankings.CONTAINS
  })
}

export function filterClusters<
  T extends AvailableK8sClusterAsset | OwnedK8sClusterAsset
>(clusters: T[], searchValue: string): T[] {
  if (!searchValue) {
    return clusters
  }

  return matchSorter(clusters, searchValue, {
    keys: ['clusterName', 'availableNamespaces'],
    threshold: matchSorter.rankings.CONTAINS
  })
}

export function filterNamespaceRequests<
  T extends OutgoingRequestByAssetId | IncomingRequestByAssetId
>(namespaceRequests: T[], searchValue: string): T[] {
  if (!searchValue) {
    return namespaceRequests
  }

  return matchSorter(namespaceRequests, searchValue, {
    keys: ['namespace'],
    threshold: matchSorter.rankings.CONTAINS
  })
}
